// Footage-clip ordering, concat preconditions and "has the footage changed
// since we combined it" — client side.
//
// Mirrors packages/functions/shared/footage_compat.py. The server is the
// authority (combine_footage re-derives every rule below before it accepts a
// request); this exists so the Footage tab can disable the CTA with a SPECIFIC
// reason, and mark the offending clip, instead of firing a call that can only
// come back as one opaque FAILED_PRECONDITION toast.
//
// Keep the two in lock-step: they encode the same rules in two deployables
// that cannot import each other. A divergence means the button says yes and
// the callable says no.

// Terminal state of the ingest probe. codec/width/height/fps are null until
// then, which is exactly why an unprobed clip cannot be compatibility-checked.
export const STATUS_READY = 'ready';

// The OTHER terminal state: the probe ran and failed, so this clip will never
// become ready on its own and the only way past it is to remove it.
//
// This distinction is the whole reason it is named separately from
// `!== STATUS_READY`. `uploading` and `probing` are also "not ready", but they
// are transient and self-resolving — a bar that is still filling. Marking those
// as faults would put a red badge on every clip of a perfectly healthy upload,
// and would tell the user to act on something that fixes itself in seconds.
// Only this status earns a warning.
export const STATUS_ERROR = 'error';

// A combine the server is running right now, read from `project.videoConcat`.
//
// Every footage mutation is refused while this holds — reorder, delete AND the
// rotation — because the worker is streaming these exact objects in this exact
// order (`delete_footage_clip` answers CONCAT_IN_FLIGHT for the same reason).
//
// Exported rather than re-derived per surface because the rotate control now
// appears on THREE of them (the Footage tab, Overview and the editor's source
// player) and they have to agree: one surface enforcing the lock while the
// other two walk around it is worse than not having the lock. The Footage tab
// ORs its own upload-in-flight state on top — that one lives in its uploader
// hook and is not observable from the other tabs.
export const isConcatInFlight = (videoConcat) =>
  ['queued', 'processing'].includes(videoConcat?.status);

// Properties every clip must agree on for a stream copy to produce a playable
// file. Same order as COMPARED_PROPERTIES server-side so the reported issue
// list reads identically on both sides.
export const COMPARED_PROPERTIES = ['codec', 'width', 'height', 'fps'];

// Frame rates are floats derived from a container's rational rate
// (30000/1001 = 29.97002997…), so exact equality would reject two files off
// the same camera. Anything coarser than this is a genuine mismatch.
export const FPS_TOLERANCE = 0.01;

// Sort sentinel for a clip whose `order` is missing or unparseable: it sinks
// to the END of the timelapse rather than silently jumping to the front.
// Mirrors ORDER_SENTINEL in footage_compat.py and _build_footage_doc.
export const ORDER_SENTINEL = 1000000;

// The accepted text form of a number, written out rather than delegated to
// `Number()`. Firestore hands back whatever was written, and `firestore.rules`
// lets the owner write these fields, so the coercion below decides the answer —
// and it must be the SAME answer the Python twins give.
//
// Neither built-in can define that grammar, because each is looser than the
// other in a different direction: `Number('')` is 0 while `int('')` raises,
// `Number('12.7')` truncates to 12 while `int('12.7')` raises, `int('1_0')` is
// 10 while `Number('1_0')` is NaN, and `int()` reads Unicode digits (`'٢١'`)
// that JS refuses. So the same two patterns are spelled out here and in
// footage_compat.py / video_concat.py: ASCII whitespace, ASCII sign, ASCII
// digits, nothing else.
//
// The 15-digit cap on integers closes the last gap: past it `Number()` rounds
// (`Number('12345678901234567')` is …568) while `int()` reads on exactly. A
// longer string is refused on all three sides, which for `order` means the
// documented ORDER_SENTINEL fallback instead of two different positions.
const INT_TEXT = /^[ \t\n\r\f\v]*[+-]?[0-9]{1,15}[ \t\n\r\f\v]*$/;
const FLOAT_TEXT = /^[ \t\n\r\f\v]*[+-]?([0-9]+(\.[0-9]*)?|\.[0-9]+)([eE][+-]?[0-9]+)?[ \t\n\r\f\v]*$/;

function asInt(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? Math.trunc(value) : null;
  if (typeof value !== 'string' || !INT_TEXT.test(value)) return null;
  return Math.trunc(Number(value));
}

function asFloat(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || !FLOAT_TEXT.test(value)) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/**
 * One footage doc reduced to the fields the concat contract uses.
 *
 * Accepts either the Firestore doc shape useProjectVideos hands back (`id`) or
 * a manifest-style entry (`videoKey`) — the two name the same thing and this
 * module compares them against each other constantly.
 */
export function normalizeClip(video) {
  const clip = video || {};
  const order = asInt(clip.order);
  return {
    videoKey: clip.videoKey || clip.id || '',
    fileName: clip.fileName || clip.videoKey || clip.id || '',
    order: order === null ? ORDER_SENTINEL : order,
    status: clip.status || null,
    sizeBytes: asInt(clip.sizeBytes) || 0,
    durationSec: asFloat(clip.durationSec),
    codec: clip.codec ?? null,
    width: asInt(clip.width),
    height: asInt(clip.height),
    fps: asFloat(clip.fps),
  };
}

/**
 * Playback order: `order` asc, then fileName, then videoKey.
 *
 * useProjectVideos only asks Firestore for `orderBy('order')`, so two clips
 * sharing an order (which a partially-failed batch can produce) would come
 * back in an arbitrary sequence. The server breaks that tie by fileName, and
 * the tie-break decides which clip is the compatibility REFERENCE — so
 * without it the client could name a different offending clip than the server.
 */
export function sortClips(videos) {
  return [...(videos || [])].map(normalizeClip).sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    if (a.fileName !== b.fileName) return a.fileName < b.fileName ? -1 : 1;
    if (a.videoKey === b.videoKey) return 0;
    return a.videoKey < b.videoKey ? -1 : 1;
  });
}

/** Per-file reasons a clip cannot be combined yet (still uploading/probing, or errored). */
export function notReadyIssues(clips) {
  return (clips || [])
    .filter((clip) => clip.status !== STATUS_READY)
    .map((clip) => ({
      videoKey: clip.videoKey,
      fileName: clip.fileName,
      status: clip.status,
    }));
}

/**
 * The subset of not-ready issues that are FAULTS rather than progress.
 *
 * Only `status: 'error'` — a probe that failed. See STATUS_ERROR: a clip still
 * uploading or probing is not-ready too, but it is on its way to ready and
 * badging it would be a false alarm the user cannot act on.
 *
 * Callers use this to decide which rows get marked; the blocking VERDICT still
 * uses the full notReady list, because a clip that is merely still probing does
 * genuinely block a combine (it just does so temporarily).
 */
export function terminalIssues(issues) {
  return (issues || []).filter((issue) => issue && issue.status === STATUS_ERROR);
}

function valuesMatch(property, expected, actual) {
  if (property === 'fps') {
    if (expected === null || actual === null) return expected === actual;
    return Math.abs(expected - actual) <= FPS_TOLERANCE;
  }
  return expected === actual;
}

/**
 * Per-file, per-property mismatches against the FIRST clip in playback order.
 *
 * The first clip is the reference on purpose (same choice as the server): it is
 * the one property set the user can see without a file inspector, because it is
 * the top of the list they are looking at.
 */
export function compatibilityIssues(clips) {
  const list = clips || [];
  if (list.length < 2) return [];
  const reference = list[0];
  const issues = [];
  for (const clip of list.slice(1)) {
    for (const property of COMPARED_PROPERTIES) {
      const expected = reference[property];
      const actual = clip[property];
      if (!valuesMatch(property, expected, actual)) {
        issues.push({
          videoKey: clip.videoKey,
          fileName: clip.fileName,
          property,
          expected,
          actual,
        });
      }
    }
  }
  return issues;
}

/**
 * The whole local precondition mirror in one call.
 *
 * `blocking` is the combine verdict; `issues` is the per-clip list keyed by
 * videoKey, shaped exactly like the `details.issues` the callable returns so
 * the list component renders a server refusal and a locally-predicted one
 * through one code path.
 *
 * Compatibility is only checked once every clip is READY: the probe writes
 * codec/width/height/fps, so running it early would report every unprobed clip
 * as mismatched on all four properties and bury the real reason (it is still
 * being read) under four wrong ones.
 */
export function combinePreconditions(videos) {
  const clips = sortClips(videos);
  if (clips.length === 0) {
    return {
      clips, notReady: [], notReadyTerminal: [], incompatible: [], issues: {}, blocking: 'NO_FOOTAGE',
    };
  }
  const notReady = notReadyIssues(clips);
  const incompatible = notReady.length > 0 ? [] : compatibilityIssues(clips);
  const issues = {};
  for (const issue of [...notReady, ...incompatible]) {
    if (!issues[issue.videoKey]) issues[issue.videoKey] = [];
    issues[issue.videoKey].push(issue);
  }
  let blocking = null;
  if (notReady.length > 0) blocking = 'FOOTAGE_NOT_READY';
  else if (incompatible.length > 0) blocking = 'FOOTAGE_INCOMPATIBLE';
  // `notReadyTerminal` is broken out so a caller can mark the rows that are
  // actually broken without also marking the ones that are merely in progress.
  return { clips, notReady, notReadyTerminal: terminalIssues(notReady), incompatible, issues, blocking };
}

/** Group a flat `details.issues` array (server or local) by videoKey. */
export function groupIssuesByKey(issues) {
  const grouped = {};
  for (const issue of issues || []) {
    if (!issue || !issue.videoKey) continue;
    if (!grouped[issue.videoKey]) grouped[issue.videoKey] = [];
    grouped[issue.videoKey].push(issue);
  }
  return grouped;
}

const NOT_READY_LABEL = {
  uploading: 'Still uploading',
  probing: 'Still being read',
  error: 'Couldn’t be read — remove this clip',
};

// COMPARED_PROPERTIES are wire/field names. These are the words a user has for
// the same things. `width` and `height` deliberately map to the SAME word: they
// are two halves of one fact the user recognises (the resolution), and nobody
// describes a 2560x1440 clip as differing in "width, height".
const PROPERTY_LABEL = {
  codec: 'format',
  fps: 'frame rate',
  width: 'resolution',
  height: 'resolution',
};

/**
 * The mismatched properties of a whole clip set, in the user's words, ready to
 * be listed in a sentence ("they differ in resolution, frame rate").
 *
 * De-duplicated BY LABEL, not by property: a clip that differs in both width
 * and height is one resolution mismatch, and "resolution, resolution" would be
 * worse than the raw field names it replaces. First-seen order is kept so the
 * sentence follows COMPARED_PROPERTIES rather than an arbitrary set order.
 */
export function describeMismatchedProperties(issues) {
  const labels = [];
  for (const issue of issues || []) {
    const property = issue && issue.property;
    if (!property) continue;
    const label = PROPERTY_LABEL[property] || property;
    if (!labels.includes(label)) labels.push(label);
  }
  return labels;
}

function formatProperty(property, value) {
  if (value === null || value === undefined || value === '') return 'unknown';
  if (property === 'fps') return `${Math.round(Number(value))} fps`;
  return String(value);
}

/**
 * One clip's issues as a sentence for its row.
 *
 * width and height are separate properties server-side (so a resolution
 * mismatch arrives as TWO issues), but "1920 — other clips are 2560" next to
 * "1080 — other clips are 1440" is unreadable. They are recombined here into
 * the one fact the user recognises: the resolution.
 */
export function describeClipIssues(issues) {
  const list = (issues || []).filter(Boolean);
  if (list.length === 0) return null;

  const notReady = list.find((issue) => !issue.property);
  if (notReady) {
    return NOT_READY_LABEL[notReady.status] || 'Not ready to combine';
  }

  const byProperty = {};
  for (const issue of list) byProperty[issue.property] = issue;
  const parts = [];
  const width = byProperty.width;
  const height = byProperty.height;
  if (width && height) {
    parts.push(
      `${formatProperty('width', width.actual)}x${formatProperty('height', height.actual)}`
      + ` — other clips are ${formatProperty('width', width.expected)}x${formatProperty('height', height.expected)}`,
    );
  } else if (width || height) {
    // Only ONE dimension differs (1920x1080 vs 1280x1080). The matching
    // dimension is not in the issue list at all, and inventing it — say by
    // reusing the other property's number — would print a resolution neither
    // clip has. Name the dimension that actually differs instead.
    const issue = width || height;
    parts.push(
      `${issue.property} ${formatProperty(issue.property, issue.actual)}`
      + ` — other clips are ${formatProperty(issue.property, issue.expected)}`,
    );
  }
  for (const property of ['codec', 'fps']) {
    const issue = byProperty[property];
    if (!issue) continue;
    parts.push(`${formatProperty(property, issue.actual)} — other clips are ${formatProperty(property, issue.expected)}`);
  }
  return parts.join(' · ');
}

/**
 * How the live footage list differs from the manifest the last combine ran on.
 *
 * videoConcat.manifest is the ordered [{videoKey, sizeBytes}] snapshot the
 * callable persisted, and it is the ONLY signal that a combined video is out of
 * date: a clip added, removed, reordered or re-uploaded after that point means
 * the mp4 no longer represents the footage. Returns `changed: false` when there
 * is no manifest at all — an absent snapshot is "can't tell", and nagging a
 * user to rebuild on a guess is worse than staying quiet.
 *
 * `resized` catches the re-upload case specifically: storage.rules lets an
 * owner overwrite an existing footage object, so the same videoKey can hold
 * different bytes than the combine consumed.
 */
export function manifestDiff(videos, manifest) {
  const entries = Array.isArray(manifest) ? manifest : null;
  const clips = sortClips(videos);
  if (!entries || entries.length === 0) {
    return { changed: false, added: [], removed: [], resized: [], reordered: false };
  }

  const manifestKeys = entries.map((entry) => entry?.videoKey).filter(Boolean);
  const manifestSizes = new Map(
    entries.filter((entry) => entry && entry.videoKey)
      .map((entry) => [entry.videoKey, asInt(entry.sizeBytes) || 0]),
  );
  const liveKeys = clips.map((clip) => clip.videoKey);
  const liveSet = new Set(liveKeys);

  const added = liveKeys.filter((key) => !manifestSizes.has(key));
  const removed = manifestKeys.filter((key) => !liveSet.has(key));
  const resized = clips
    .filter((clip) => manifestSizes.has(clip.videoKey)
      && manifestSizes.get(clip.videoKey) !== clip.sizeBytes)
    .map((clip) => clip.videoKey);

  // Order is compared over the SHARED clips only. Otherwise every addition
  // would also report a reorder (the new clip shifts the sequence), and
  // "1 new clip · reordered" is two claims for one event.
  const sharedLive = liveKeys.filter((key) => manifestSizes.has(key));
  const sharedManifest = manifestKeys.filter((key) => liveSet.has(key));
  const reordered = sharedLive.length === sharedManifest.length
    && sharedLive.some((key, index) => key !== sharedManifest[index]);

  return {
    changed: added.length > 0 || removed.length > 0 || resized.length > 0 || reordered,
    added,
    removed,
    resized,
    reordered,
  };
}
